import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import type { Convite } from './types';

interface ConvitesContextValue {
  convites: Convite[];
  carregando: boolean;
  recarregar: () => void;
  aceitar: (eventoId: number) => Promise<string | null>;
  recusar: (eventoId: number) => Promise<string | null>;
  removerLocal: (eventoId: number) => void;
}

const ConvitesContext = createContext<ConvitesContextValue | null>(null);

export function ConvitesProvider({ children }: { children: ReactNode }) {
  const { user, pronto } = useAuth();
  const [convites, setConvites] = useState<Convite[]>([]);
  const [carregando, setCarregando] = useState(true);

  const buscar = useCallback(() => {
    // Fonte única de verdade: o AuthProvider. Sem getUser() de rede, então uma
    // oscilação de sinal não esvazia os convites nem parece "deslogado".
    if (!pronto) return;
    if (!user) {
      setConvites([]);
      setCarregando(false);
      return;
    }
    setCarregando(true);
    supabase
      .from('vw_meus_convites')
      .select('*')
      .then(({ data }) => {
        setConvites((data as Convite[]) || []);
        setCarregando(false);
      });
  }, [pronto, user]);

  useEffect(() => {
    buscar();
  }, [buscar]);

  const removerLocal = useCallback((eventoId: number) => {
    setConvites((prev) => prev.filter((c) => c.evento_id !== eventoId));
  }, []);

  const aceitar = useCallback(
    async (eventoId: number): Promise<string | null> => {
      const { error } = await supabase.rpc('aceitar_convite', { p_evento_id: eventoId });
      if (error) return mensagemAmigavel(error);
      removerLocal(eventoId);
      return null;
    },
    [removerLocal],
  );

  const recusar = useCallback(
    async (eventoId: number): Promise<string | null> => {
      const { error } = await supabase.rpc('recusar_convite', { p_evento_id: eventoId });
      if (error) return mensagemAmigavel(error);
      removerLocal(eventoId);
      return null;
    },
    [removerLocal],
  );

  const valor: ConvitesContextValue = {
    convites,
    carregando,
    recarregar: buscar,
    aceitar,
    recusar,
    removerLocal,
  };

  return <ConvitesContext.Provider value={valor}>{children}</ConvitesContext.Provider>;
}

export function useConvites(): ConvitesContextValue {
  const ctx = useContext(ConvitesContext);
  if (!ctx) {
    throw new Error('useConvites deve ser usado dentro de ConvitesProvider');
  }
  return ctx;
}